// ChatPage.jsx
import React, { useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  addDoc,
  collection,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { UserContext } from "../context/UserContext";
import { FiMessageSquare, FiPlus, FiSmile, FiX } from "react-icons/fi";
import { BsFileEarmarkCode, BsFileZip } from "react-icons/bs";
import { MdImage } from "react-icons/md";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import Picker from "emoji-picker-react";
import { toast } from "react-toastify";

const storage = getStorage();

// ---- Utility: time ago formatter ----
const getTimeAgo = (timestamp) => {
  if (!timestamp) return "";
  const date = timestamp.toDate?.() || new Date(timestamp);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);

  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

const ChatPage = () => {
  const [chatRooms, setChatRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const { user } = useContext(UserContext);
  const [newMessage, setNewMessage] = useState("");
  const [showRoomsSidebar, setShowRoomsSidebar] = useState(false);
  const [showChatMenu, setShowChatMenu] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const messagesEndRef = useRef(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [previewFiles, setPreviewFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Fetch chat rooms
  useEffect(() => {
    if (!user?.id) return;
    const q = query(
      collection(db, "chatRooms"),
      orderBy("lastMessageAt", "desc")
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const rooms = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((room) => room.participants?.includes(user.id));
      setChatRooms(rooms);
    });
    return () => unsubscribe();
  }, [user]);

  // Fetch messages
  useEffect(() => {
    if (!selectedRoom) return;
    const q = query(
      collection(db, "chatRooms", selectedRoom.id, "messages"),
      orderBy("createdAt", "asc")
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setMessages(msgs);
    });
    return () => unsubscribe();
  }, [selectedRoom]);

  // ----------- SEND MESSAGE ----------- //
  const sendMessage = async (
    type = "text",
    content = null,
    fileName = null
  ) => {
    if (!selectedRoom) return;

    await addDoc(collection(db, "chatRooms", selectedRoom.id, "messages"), {
      senderId: user.id,
      senderName: user.name || "User",
      type,
      content: content || newMessage,
      fileName: fileName || null,
      createdAt: serverTimestamp(),
    });

    setNewMessage("");
    setShowEmojiPicker(false);
  };

  // File upload handler
  const handleFileUpload = async (file) => {
    setShowChatMenu(false);
    if (!file) return;
    const fileRef = ref(storage, `chatFiles/${Date.now()}-${file.name}`);
    await uploadBytes(fileRef, file);
    const url = await getDownloadURL(fileRef);
    const type = file.type.includes("image") ? "image" : "file";
    await sendMessage(type, url, file.name);
  };

  // Code snippet
  const handleCodeSnippet = async () => {
    setShowChatMenu(false);
    const snippet = prompt("Paste your code snippet:");
    if (snippet) await sendMessage("code", snippet);
  };

  // Emoji

  const handleSend = async () => {
    if (!newMessage.trim() && previewFiles.length === 0) return;

    setUploading(true);

    // Send text message
    if (newMessage.trim()) {
      await sendMessage("text", newMessage);
    }

    // Send preview files
    for (const item of previewFiles) {
      if (item.type === "image") {
        await handleFileUpload(item.file);
      } else if (item.type === "file") {
        await handleFileUpload(item.file);
      } else if (item.type === "code") {
        await sendMessage("code", item.content);
      }
    }

    setNewMessage("");
    setPreviewFiles([]);
    setUploading(false);
  };
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const previews = files.map((file) => ({
      file,
      type: file.type.startsWith("image") ? "image" : "file",
      url: URL.createObjectURL(file),
    }));
    setPreviewFiles([...previewFiles, ...previews]);
  };

  const handleEmojiSelect = (emoji) => {
    setMessage((prev) => prev + emoji.native); // FIXED undefined issue
    setShowEmoji(false);
  };

  const handleEmojiClick = (emojiData) => {
    // Some versions return emojiData.emoji, some return emojiData.native
    const emojiChar = emojiData.emoji || emojiData.native || "";
    setNewMessage((prev) => prev + emojiChar);
    setShowEmojiPicker(false); // close after selection
  };
  return (
    <Container>
      {/* Chat Rooms Sidebar */}
      <ChatRoomsList show={showRoomsSidebar || !selectedRoom}>
        <Header>
          Code Issue Chat Groups
          {showRoomsSidebar && (
            <CloseButton onClick={() => setShowRoomsSidebar(false)}>
              ×
            </CloseButton>
          )}
        </Header>
        {chatRooms.map((room) => (
          <ChatRoomItem
            key={room.id}
            onClick={() => {
              setSelectedRoom(room);
              setShowRoomsSidebar(false);
            }}
            active={selectedRoom?.id === room.id}
          >
            <RoomTop>
              <RoomTitle>{"💬 " + room.issueTitle || "Untitled"}</RoomTitle>
              <RoomMeta>
                {getTimeAgo(room.lastMessageAt || room.createdAt)}
              </RoomMeta>
            </RoomTop>
            <RoomLastMessage>
              {room.lastMessageText
                ? `${room.lastMessageSender || "User"}: ${room.lastMessageText}`
                : "No messages yet"}
            </RoomLastMessage>
          </ChatRoomItem>
        ))}
      </ChatRoomsList>

      {/* Chat Window */}
      <ChatWindow show={!!selectedRoom}>
        {selectedRoom ? (
          <>
            <ChatHeader>
              <HeaderTitle>{selectedRoom.issueTitle}</HeaderTitle>

              <HeaderIcons>
                <FiMessageSquare
                  size={22}
                  style={{ cursor: "pointer" }}
                  onClick={() => setShowRoomsSidebar(true)}
                />
              </HeaderIcons>
            </ChatHeader>
            <Messages>
              {messages.map((msg) => (
                <Message key={msg.id} isOwn={msg.senderId === user.id}>
                  <SenderName isOwn={msg.senderId === user.id}>
                    {msg.senderName}
                  </SenderName>
                  <MessageBubble isOwn={msg.senderId === user.id}>
                    {msg.type === "text" && msg.content}
                    {msg.type === "emoji" && (
                      <span style={{ fontSize: "24px" }}>{msg.content}</span>
                    )}
                    {msg.type === "image" && (
                      <img
                        src={msg.content}
                        alt="chat-img"
                        style={{ maxWidth: "200px", borderRadius: "6px" }}
                      />
                    )}
                    {msg.type === "file" && (
                      <a
                        href={msg.content}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        📎 {msg.fileName}
                      </a>
                    )}
                    {msg.type === "code" && (
                      <CodeContainer>
                        <pre
                          style={{
                            background: "#2a2a2a",
                            padding: "10px",
                            borderRadius: "6px",
                            maxWidth: "100%", // don't exceed bubble width
                            overflowX: "auto", // allow horizontal scroll if needed
                            whiteSpace: "pre-wrap", // wrap long lines
                            wordBreak: "break-word", // break long words if necessary
                          }}
                        >
                          <code>{msg.content}</code>
                          <CopyButton
                            onClick={() => {
                              navigator.clipboard.writeText(msg.content);
                              toast.success("code coppied");
                            }}
                          >
                            📋
                          </CopyButton>
                        </pre>
                      </CodeContainer>
                    )}
                  </MessageBubble>
                  <MessageTime isOwn={msg.senderId === user.id}>
                    {`sent ${getTimeAgo(msg.createdAt)}`}
                  </MessageTime>
                </Message>
              ))}

              <div ref={messagesEndRef} />
            </Messages>
          </>
        ) : (
          <EmptyState>Select a Code Issue Chat Group</EmptyState>
        )}

        {selectedRoom && (
          <ChatBox>
            {/* Attachments */}
            <AttachmentMenu>
              {showChatMenu ? (
                <FiX
                  onClick={() => setShowChatMenu(false)}
                  size={22}
                  style={{ cursor: "pointer" }}
                />
              ) : (
                <FiPlus
                  onClick={() => setShowChatMenu(true)}
                  size={22}
                  style={{ cursor: "pointer" }}
                />
              )}

              {showChatMenu && (
                <AttachmentOptions>
                  <Option>
                    <label>
                      <MdImage /> Image
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const preview = {
                              file,
                              type: "image",
                              url: URL.createObjectURL(file),
                            };
                            setPreviewFiles((prev) => [...prev, preview]);
                            setShowChatMenu(false); // CLOSE menu after selection
                          }
                        }}
                      />
                    </label>
                  </Option>
                  <Option>
                    <label>
                      <BsFileZip /> Zip
                      <input
                        type="file"
                        accept=".zip"
                        hidden
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const preview = {
                              file,
                              type: "file",
                              url: null,
                            };
                            setPreviewFiles((prev) => [...prev, preview]);
                            setShowChatMenu(false); // CLOSE menu after selection
                          }
                        }}
                      />
                    </label>
                  </Option>
                  <Option
                    onClick={() => {
                      handleCodeSnippet();
                      setShowChatMenu(false); // close after code snippet
                    }}
                  >
                    <BsFileEarmarkCode /> Code
                  </Option>
                  <Option
                    onClick={() => {
                      setShowEmojiPicker((prev) => !prev);
                      setShowChatMenu(false); // close attachment menu when opening emoji
                    }}
                  >
                    <FiSmile /> Emoji
                  </Option>
                </AttachmentOptions>
              )}
            </AttachmentMenu>

            {/* Preview Files - ALWAYS ABOVE Input */}
            {previewFiles.length > 0 && (
              <FilePreviewContainer>
                {previewFiles.map((item, idx) => (
                  <PreviewItem key={idx}>
                    {item.type === "image" && (
                      <img src={item.url} alt="preview" width="60" />
                    )}
                    {item.type === "code" && (
                      <pre>
                        <code>{item.content}</code>
                      </pre>
                    )}
                    {item.type === "file" && <span>📎 {item.file.name}</span>}
                    <FiX
                      size={16}
                      style={{
                        cursor: "pointer",
                        position: "absolute",
                        top: 2,
                        right: 2,
                      }}
                      onClick={() =>
                        setPreviewFiles((prev) =>
                          prev.filter((_, i) => i !== idx)
                        )
                      }
                    />
                  </PreviewItem>
                ))}
              </FilePreviewContainer>
            )}

            {/* Message Input */}
            <MessageInput
              type="text"
              placeholder="Type your message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
            />
            <SendButton onClick={handleSend}>Send</SendButton>

            {/* Emoji Picker */}
            {showEmojiPicker && (
              <EmojiWrapper>
                <Picker onEmojiClick={handleEmojiClick} />
              </EmojiWrapper>
            )}
          </ChatBox>
        )}
      </ChatWindow>
    </Container>
  );
};

export default ChatPage;

/* ---------- Styled Components ---------- */
/* ---------- Glassy Gradient-Themed ChatPage Styles ---------- */

const Container = styled.div`
  display: flex;
  height: 88vh;
  background: radial-gradient(circle at top left, #0b0617, #000);
  color: #fff;
  overflow: hidden;
  border-radius: 16px;
  box-shadow: 0 0 40px rgba(155, 92, 255, 0.15);
`;

/* Sidebar / Chat Rooms */
const ChatRoomsList = styled.div`
  width: 300px;
  border-right: 1px solid rgba(155, 92, 255, 0.15);
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: ${(props) => (props.show ? "0" : "-100%")};
    height: 100%;
    z-index: 100;
    transition: left 0.3s ease-in-out;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(155, 92, 255, 0.2);
    border-radius: 10px;
  }
`;

const Header = styled.div`
  padding: 18px;
  font-weight: 600;
  font-size: 1rem;
  text-align: center;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  border-bottom: 1px solid rgba(155, 92, 255, 0.15);
  position: sticky;
  top: 0;
  z-index: 10;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #aaa;
  font-size: 20px;
  cursor: pointer;
  transition: 0.2s;

  &:hover {
    color: #fff;
  }
`;

const ChatRoomItem = styled.div`
  padding: 14px;
  margin: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  background: ${(props) =>
    props.active
      ? "linear-gradient(90deg, rgba(155,92,255,0.35), rgba(197,159,255,0.15))"
      : "rgba(255,255,255,0.03)"};
  transition: all 0.25s ease;

  &:hover {
    background: rgba(155, 92, 255, 0.15);
    transform: translateY(-2px);
  }
`;

const RoomTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const RoomTitle = styled.span`
  font-weight: 600;
  font-size: 15px;
  color: #fff;
`;

const RoomMeta = styled.span`
  font-size: 12px;
  color: #aaa;
`;

const RoomLastMessage = styled.div`
  margin-top: 6px;
  font-size: 13px;
  color: #ccc;
  opacity: 0.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* Chat Window */
const ChatWindow = styled.div`
  flex: 1;
  display: ${(props) => (props.show ? "flex" : "none")};
  flex-direction: column;
  overflow: hidden;
  backdrop-filter: blur(8px);

  @media (min-width: 769px) {
    display: flex;
  }
`;

const ChatHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid rgba(155, 92, 255, 0.15);
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const HeaderTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const HeaderIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Messages = styled.div`
  flex: 1;
  padding: 18px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  background: rgba(0, 0, 0, 0.25);

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(155, 92, 255, 0.2);
    border-radius: 10px;
  }
`;

const Message = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.isOwn ? "flex-end" : "flex-start")};
  margin-bottom: 14px;
`;

const SenderName = styled.span`
  font-size: 11px;
  color: #aaa;
  margin-bottom: 4px;
`;

const MessageBubble = styled.div`
  padding: 10px 14px;
  border-radius: 14px;
  background: ${(props) =>
    props.isOwn
      ? "linear-gradient(135deg, rgba(155,92,255,0.9), rgba(197,159,255,0.8))"
      : "rgba(255,255,255,0.08)"};
  color: #fff;
  max-width: 70%;
  word-wrap: break-word;
  box-shadow: ${(props) =>
    props.isOwn
      ? "0 0 10px rgba(155,92,255,0.3)"
      : "0 0 10px rgba(255,255,255,0.05)"};
  transition: 0.2s;
  backdrop-filter: blur(6px);

  img {
    max-width: 200px;
    border-radius: 10px;
  }

  a {
    color: #c59fff;
    text-decoration: none;
  }
`;

const MessageTime = styled.span`
  font-size: 10px;
  color: #888;
  margin-top: 4px;
`;

const CodeContainer = styled.div`
  position: relative;
  background: #0f0f0f;
  border-radius: 8px;
  padding: 10px;
  border: 1px solid rgba(155, 92, 255, 0.25);
  color: #c5c5c5;
  font-family: "Fira Code", monospace;
  overflow-x: auto;

  pre {
    margin: 0;
  }
`;

const CopyButton = styled.button`
  position: absolute;
  top: 6px;
  right: 8px;
  background: rgba(155, 92, 255, 0.8);
  border: none;
  border-radius: 4px;
  padding: 2px 6px;
  color: #fff;
  font-size: 12px;
  cursor: pointer;
  transition: 0.2s;

  &:hover {
    background: rgba(197, 159, 255, 1);
  }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #aaa;
  text-align: center;
`;

const ChatBox = styled.div`
  padding: 12px 16px;
  border-top: 1px solid rgba(155, 92, 255, 0.15);
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.05);
  position: relative;
`;

const MessageInput = styled.input`
  flex: 1;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(155, 92, 255, 0.15);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  transition: 0.25s;

  &:focus {
    outline: none;
    border: 1px solid rgba(155, 92, 255, 0.5);
    box-shadow: 0 0 10px rgba(155, 92, 255, 0.2);
  }
`;

const SendButton = styled.button`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: #fff;
  border: none;
  padding: 10px 16px;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 0 20px rgba(155, 92, 255, 0.3);
  }
`;

const AttachmentMenu = styled.div`
  position: relative;
  color: #c59fff;
`;

const AttachmentOptions = styled.div`
  position: absolute;
  bottom: 45px;
  left: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: rgba(20, 20, 20, 0.95);
  padding: 8px;
  border-radius: 10px;
  border: 1px solid rgba(155, 92, 255, 0.25);
  z-index: 10;
`;

const Option = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 13px;
  color: #fff;
  padding: 6px 10px;
  border-radius: 8px;
  transition: 0.2s ease;

  &:hover {
    background: rgba(155, 92, 255, 0.25);
  }
`;

const FilePreviewContainer = styled.div`
  display: flex;
  gap: 10px;
  padding: 6px 0;
  flex-wrap: wrap;
`;

const PreviewItem = styled.div`
  position: relative;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    border-radius: 6px;
  }
`;

const EmojiWrapper = styled.div`
  position: absolute;
  bottom: 60px;
  right: 20px;
  z-index: 100;
  background: rgba(20, 20, 20, 0.95);
  border-radius: 10px;
  border: 1px solid rgba(155, 92, 255, 0.25);
  overflow: hidden;
`;
